/**
 * CSOApplicationService - Podnošenje zahteva za registraciju, admin odobravanje/odbijanje.
 */

import ApiService, { clearLoginDataCache } from '@/core/api/ApiService';
import { API_ENDPOINTS } from '@/core/api/endpoints';
import OrganizationService, {
  normalizeOrganizationAdditionalInfo,
} from '@/shared/services/OrganizationService';
import CSORegisterCatalogService from './CSORegisterCatalogService';
import {
  buildOrganizationAddApiBody,
  resolveOrganizationAddDefaults,
} from './organizationAddPayload';
import { buildOrganizationRegistrationApiBody } from './organizationRegistrationPayload';
import { toApiError } from '@/shared/utils/apiErrorMessage';

class CSOApplicationService {
  /**
   * Građanska prijava OCD: POST /cso/register (org + team lead + kontakt u jednom pozivu).
   * @param {{ name: string, userEmail?: string, userPhone?: string, contactFirstName?: string, contactLastName?: string, first_name?: string, last_name?: string, email?: string, phone?: string }} data
   */
  async registerOrganization(data) {
    try {
      const response = await ApiService.post(
        API_ENDPOINTS.CSO.REGISTER_BY_USER,
        buildOrganizationRegistrationApiBody(data),
      );
      const created = response.data ?? response;
      const organizationId = created?.id ?? created?.organization_id;

      if (!organizationId) {
        throw new Error('Сервер није вратио ID нове организације');
      }

      clearLoginDataCache();
      return created;
    } catch (error) {
      if (error.response && error.response.status === 401) throw new Error('Korisnik nije prijavljen');
      if (error.response && error.response.status === 400) {
        throw toApiError(error, 'Neispravni podaci organizacije');
      }
      throw error;
    }
  }

  /**
   * Admin / legacy: POST /cso/add, zatim POST /organization/save-additional-info (kontakt).
   * @param {{ name: string, registration_number?: string, register_id?: string, userEmail?: string, userPhone?: string, contactFirstName?: string, contactLastName?: string, first_name?: string, last_name?: string, email?: string, phone?: string }} data
   */
  async addOrganization(data) {
    try {
      if (!data?.name?.trim()) throw new Error('Naziv organizacije je obavezan');

      const registerDefaults = await resolveOrganizationAddDefaults(
        data,
        () => CSORegisterCatalogService.getActive(),
      );

      const response = await ApiService.post(
        API_ENDPOINTS.CSO.ADD,
        buildOrganizationAddApiBody(data, registerDefaults),
      );
      const created = response.data ?? response;
      const organizationId = created?.id ?? created?.organization_id;

      if (!organizationId) {
        throw new Error('Сервер није вратио ID нове организације');
      }

      const additional = normalizeOrganizationAdditionalInfo(data);
      const hasAdditional =
        additional.first_name ||
        additional.last_name ||
        additional.phone ||
        additional.email;

      if (hasAdditional) {
        try {
          await OrganizationService.saveAdditionalInfo({
            organization_id: organizationId,
            ...additional,
          });
        } catch (additionalError) {
          const msg =
            additionalError.message ||
            'Грешка при чувању контакт података организације';
          throw new Error(
            `${msg} (организација је креирана, ID: ${organizationId})`
          );
        }
      }

      return created;
    } catch (error) {
      if (error.response && error.response.status === 401) throw new Error('Korisnik nije prijavljen');
      if (error.response && error.response.status === 400) {
        throw toApiError(error, 'Neispravni podaci organizacije');
      }
      throw error;
    }
  }

  /**
   * Admin edit: PUT /cso/{id} (naziv + register metadata), zatim kontakt preko save-additional-info u wizard-u.
   * @param {string} organizationId
   * @param {{ name: string, register_id?: string, registerId?: string, registration_number?: string, registrationNumber?: string }} data
   */
  async updateOrganization(organizationId, data) {
    try {
      if (!organizationId) throw new Error('ID organizacije je obavezan');
      if (!data?.name?.trim()) throw new Error('Naziv organizacije je obavezan');

      const payload = buildOrganizationAddApiBody(data, {
        registerId: data.register_id?.trim() || data.registerId?.trim() || undefined,
        registrationNumber:
          data.registration_number?.trim() ||
          data.registrationNumber?.trim() ||
          undefined,
      });

      const response = await ApiService.put(API_ENDPOINTS.CSO.UPDATE(organizationId), payload);
      if (response.data) return response.data;
      return { success: true, id: organizationId };
    } catch (error) {
      if (error.response && error.response.status === 401) throw new Error('Korisnik nije prijavljen');
      if (error.response && error.response.status === 404) throw new Error('Organizacija nije pronađena');
      if (error.response && error.response.status === 400) {
        throw toApiError(error, 'Neispravni podaci organizacije');
      }
      throw error;
    }
  }

  async approveOrganization(organizationId) {
    try {
      if (!organizationId) throw new Error('ID organizacije je obavezan');
      const response = await ApiService.post(API_ENDPOINTS.CSO.APPROVE, { organization_id: organizationId });
      if (response.data) return response.data;
      return { success: true };
    } catch (error) {
      if (error.response && error.response.status === 401) throw new Error('Korisnik nije prijavljen');
      if (error.response && error.response.status === 403) throw new Error('Nemate dozvolu za odobravanje organizacija');
      if (error.response && error.response.status === 404) throw new Error('Organizacija nije pronađena');
      if (error.response && error.response.status === 400) throw new Error(error.response.data?.message || 'Greška pri odobravanju organizacije');
      throw error;
    }
  }

  async rejectOrganization(organizationId) {
    try {
      if (!organizationId) throw new Error('ID organizacije je obavezan');
      const response = await ApiService.post(API_ENDPOINTS.CSO.REJECT_NEW, { organization_id: organizationId });
      if (response.data) return response.data;
      return { success: true };
    } catch (error) {
      if (error.response && error.response.status === 401) throw new Error('Korisnik nije prijavljen');
      if (error.response && error.response.status === 403) throw new Error('Nemate dozvolu za odbijanje organizacija');
      if (error.response && error.response.status === 404) throw new Error('Organizacija nije pronađena');
      if (error.response && error.response.status === 400) throw new Error(error.response.data?.message || 'Greška pri odbijanju organizacije');
      throw error;
    }
  }

  async invalidateOrganization(organizationId) {
    try {
      if (!organizationId) throw new Error('ID organizacije je obavezan');
      const response = await ApiService.post(API_ENDPOINTS.CSO.INVALIDATE, { organization_id: organizationId });
      if (response.data) return response.data;
      return { success: true };
    } catch (error) {
      if (error.response && error.response.status === 401) throw new Error('Korisnik nije prijavljen');
      if (error.response && error.response.status === 403) throw new Error('Nemate dozvolu za invalidaciju organizacija');
      if (error.response && error.response.status === 404) throw new Error('Organizacija nije pronađena');
      if (error.response && error.response.status === 400) throw new Error(error.response.data?.message || 'Greška pri invalidaciji organizacije');
      throw error;
    }
  }

  async getApprovalList() {
    try {
      const response = await ApiService.get(API_ENDPOINTS.CSO.APPROVAL_LIST);
      if (response.data) {
        if (response.data.csos && Array.isArray(response.data.csos)) {
          return response.data.csos.map(cso => ({
            id: cso.id,
            name: cso.name,
            naziv: cso.name,
            registrationNumber: cso.registration_number,
            registration_number: cso.registration_number,
            maticniBroj: cso.registration_number,
            register_id: cso.register_id,
            registry: cso.register_name,
            register_name: cso.register_name,
            registar: cso.register_name,
            ...cso
          }));
        }
        if (Array.isArray(response.data)) return response.data;
      }
      return [];
    } catch (error) {
      if (error.response && error.response.status === 401) throw new Error('Korisnik nije prijavljen');
      throw error;
    }
  }
}

const csoApplicationServiceInstance = new CSOApplicationService();
export default csoApplicationServiceInstance;
